import { fetchApi } from './client'
import type { Run, RunDetail } from './types'

export interface RunFilters {
  status?: string[]
  workflow?: string[]
  page?: number
  pageSize?: number
}

/**
 * Fetch list of runs with optional filters.
 *
 * Note: This maps to the backend's /data endpoint which returns
 * dashboard data including recent runs. The actual endpoint may
 * vary - adjust as needed when integrating.
 */
export async function getRuns(filters?: RunFilters): Promise<Run[]> {
  // Build query params
  const params = new URLSearchParams()
  if (filters?.status?.length) {
    params.set('status', filters.status.join(','))
  }
  if (filters?.workflow?.length) {
    params.set('workflow', filters.workflow.join(','))
  }
  if (filters?.page) {
    params.set('page', String(filters.page))
  }
  if (filters?.pageSize) {
    params.set('pageSize', String(filters.pageSize))
  }

  const query = params.toString()
  const endpoint = query ? `/api/v1/data?${query}` : '/api/v1/data'

  // Backend /api/v1/data returns { uploads: [...] } structure
  const response = await fetchApi<{ uploads: Run[] }>(endpoint)
  return response.uploads || []
}

/**
 * Fetch single run by fileId.
 *
 * Maps to /api/v1/status/:fileId endpoint.
 */
export async function getRun(fileId: string): Promise<RunDetail> {
  const response = await fetchApi<RunDetail>(`/api/v1/status/${fileId}`)
  return response
}

/**
 * Fetch extraction results for a completed run.
 *
 * Maps to /api/v1/results/:uploadID endpoint.
 */
export async function getRunResults(fileId: string): Promise<unknown> {
  return fetchApi(`/api/v1/results/${fileId}`)
}

/**
 * Retry a failed run.
 *
 * Maps to /api/v1/rerun-upload endpoint.
 */
export async function retryRun(fileId: string): Promise<{ message: string }> {
  return fetchApi<{ message: string }>('/api/v1/rerun-upload', {
    method: 'POST',
    body: { uploadID: fileId },
  })
}

/**
 * Delete a run.
 *
 * Maps to DELETE /api/v1/runs/:fileId endpoint.
 * Note: Backend may need this endpoint created.
 */
export async function deleteRun(fileId: string): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/v1/runs/${fileId}`, {
    method: 'DELETE',
  })
}

/**
 * Delete multiple runs (bulk action).
 *
 * Note: Backend may need this endpoint. Falls back to sequential deletes if bulk not available.
 */
export async function deleteRuns(fileIds: string[]): Promise<{ message: string }> {
  try {
    // Try bulk endpoint first
    return await fetchApi<{ message: string }>('/api/v1/runs/bulk-delete', {
      method: 'POST',
      body: { fileIds },
    })
  } catch {
    // Fallback: delete sequentially
    await Promise.all(fileIds.map((id) => deleteRun(id)))
    return { message: `Deleted ${fileIds.length} runs` }
  }
}

/**
 * Retry multiple runs (bulk action).
 *
 * Note: Backend may need this endpoint. Falls back to sequential retries if bulk not available.
 */
export async function retryRuns(fileIds: string[]): Promise<{ message: string }> {
  try {
    // Try bulk endpoint first
    return await fetchApi<{ message: string }>('/api/v1/runs/bulk-retry', {
      method: 'POST',
      body: { fileIds },
    })
  } catch {
    // Fallback: retry sequentially
    await Promise.all(fileIds.map((id) => retryRun(id)))
    return { message: `Retried ${fileIds.length} runs` }
  }
}
